<template>
  <div class="content-with-spacing">
    <div v-if="ready && meetingForm" id="meeting-form-page">
      <section class="heading-container">
        <div class="heading-content">
          <div class="title-container">
            <div class="meeting-title-status">
              <h1>{{ meetingForm.name }}</h1>
              <span v-if="meetingMarkedComplete || !requiresLearnerSignature" class="meeting-completed">
                <i class="fas fa-check" aria-hidden="true"></i> Meeting Completed
              </span>
            </div>
            <p class="description" v-html="getMarkdown(meetingForm.description)"></p>
          </div>
        </div>
      </section>
      <section>
        <empty-placeholder v-if="!latestSubmission" title="No submission" info="The meeting form has not been submitted yet."></empty-placeholder>
        <k-meeting-form v-else
          ref="meetingForm"
          :questionnaire="meetingForm"
          :latest-submission="latestSubmission"
          :read-only="true"
          :disable-text-toolbar="true"
        ></k-meeting-form>
        <meeting-form-declaration
          v-if="latestSubmission"
          v-model="declarationChecked"
          :show-declaration="showDeclaration"
          :show-signing-button="true"
          :disable-checkbox="latestSubmissionSignedByCurrentUser"
          :disable-button="!declarationChecked || latestSubmissionSignedByCurrentUser"
          @button-click="signMeetingForm"
        ></meeting-form-declaration>
      </section>
    </div>
  </div>
</template>

<style scoped>
.heading-content {
  width: 100%;
}

.meeting-title-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.meeting-completed {
  color: var(--kate-type-success) !important;
  font-size: 2em;
}
</style>

<script>
import useGlobalStore from '@stores/global';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import { markdown } from '@utils/maths-markdown';
import KMeetingForm from '../k-questionnaire.vue';
import MeetingFormDeclaration from './meeting-form-declaration.vue';

export default {
  components: {
    KMeetingForm,
    EmptyPlaceholder,
    MeetingFormDeclaration,
  },

  data() {
    return {
      store: useGlobalStore(),
      meetingFormReady: false,
      meetingForm: undefined,
      latestSubmissionReady: false,
      latestSubmission: undefined,
      isSubmitting: false,
      declarationChecked: false,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getMeetingForm();
  },

  watch: {
    ready(val) {
      if (val) {
        this.$Loading.finish();
      } else {
        this.$Loading.start();
      }
    },
    isSubmitting(val) {
      if (val) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },
    latestSubmission() {
      this.declarationChecked = this.latestSubmissionSignedByCurrentUser;
    },
  },

  computed: {
    ready() {
      return this.meetingFormReady && this.latestSubmissionReady && !this.isSubmitting;
    },
    moduleMeetingId() {
      return parseInt(this.$route.params.moduleMeetingId, 10);
    },
    studentId() {
      return this.store.userId;
    },
    meetingFormEndpoint() {
      return `/api/curriculum/meeting/${this.moduleMeetingId}/form`;
    },
    getSubmissionEndpoint() {
      return `/api/curriculum/meeting/${this.moduleMeetingId}/submission`;
    },
    latestSubmissionSignedByCurrentUser() {
      if (!this.latestSubmission || !Array.isArray(this.latestSubmission.signatures)) {
        return false;
      }
      return this.latestSubmission.signatures.some(sig => sig?.signee_user_id === parseInt(this.store.userId, 10));
    },
    meetingMarkedComplete() {
      return Boolean(this.latestSubmission?.completed_on);
    },
    requiresLearnerSignature() {
      return this.meetingForm?.requires_learner_signature;
    },
    showDeclaration() {
      return this.requiresLearnerSignature && !this.meetingMarkedComplete;
    },
  },

  methods: {
    signingEndpoint(submissionId) {
      return `/api/curriculum/meeting/${this.moduleMeetingId}/submission/${submissionId}/signature`;
    },

    getMeetingForm() {
      this.$logger.info('Getting meeting form info', { moduleMeetingId: this.moduleMeetingId }, true);
      this.$http.get(this.meetingFormEndpoint).then(response => {
        this.$logger.info('Got meeting form info', { moduleMeetingId: this.moduleMeetingId });
        this.meetingForm = response.data;
        this.meetingFormReady = true;
        this.getLatestSubmission();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Meeting form not found', { moduleMeetingId: this.moduleMeetingId }, err);
        } else {
          this.$logger.error('Error getting meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
          this.$ktoast.showError(err, true);
        }
      });
    },

    getLatestSubmission() {
      this.latestSubmissionReady = false;
      this.$logger.info('Getting latest meeting form submission', { moduleMeetingId: this.moduleMeetingId }, true);
      this.$http.get(this.getSubmissionEndpoint).then(response => {
        this.$logger.info('Got latest meeting form submission', { moduleMeetingId: this.moduleMeetingId });
        this.latestSubmission = response.data;
      }).catch(err => {
        if (this.$http.isWarning(err) || err.response.status === 403) {
          this.$router.push({ name: '403' });
          this.$logger.warn('Latest meeting form submission not found', { moduleMeetingId: this.moduleMeetingId }, err);
        } else {
          this.$logger.autowarn('Error getting latest meeting form submission', { moduleMeetingId: this.moduleMeetingId }, err);
          this.$ktoast.showError(err, true);
        }
      }).then(() => {
        this.latestSubmissionReady = true;
      });
    },

    signatureRequest(submissionId) {
      this.$logger.info('Signing meeting form', { moduleMeetingId: this.moduleMeetingId }, true);
      return this.$http.post(this.signingEndpoint(submissionId))
        .then(() => {
          this.$logger.info('Meeting form signed', { moduleMeetingId: this.moduleMeetingId });
        })
        .catch(err => {
          this.$logger.autowarn('Error signing meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
          throw err;
        });
    },

    meetingCompletionRequest() {
      this.$logger.info('Completing meeting', { moduleMeetingId: this.moduleMeetingId }, true);
      return this.$http.put(`/api/curriculum/meeting/${this.moduleMeetingId}/complete`)
        .then(res => {
          this.$logger.info('Meeting completed', { moduleMeetingId: this.moduleMeetingId });
          this.$sidebar.updateCompletionStatus('meeting', this.moduleMeetingId, true);
          this.$ktoast.showBadgesAchieved(res.data.badges_achieved);
        })
        .catch(err => {
          this.$logger.autowarn('Error completing meeting', { moduleMeetingId: this.moduleMeetingId }, err);
          throw err;
        });
    },

    async signMeetingForm() {
      this.isSubmitting = true;
      if (!this.latestSubmission?.meeting_form_submission_id) {
        this.$logger.error('Cannot sign meeting form as no submission is available.', { moduleMeetingId: this.moduleMeetingId });
        this.$ktoast.showError('Cannot sign meeting form, no submission found.', true);
        this.isSubmitting = false;
        return;
      }

      try {
        this.$logger.info('Signing meeting form', { moduleMeetingId: this.moduleMeetingId }, true);
        await this.signatureRequest(this.latestSubmission.meeting_form_submission_id);
        await this.meetingCompletionRequest();
        this.$ktoast.success('Meeting form signed');
        this.getLatestSubmission();
      } catch (err) {
        this.$logger.error('Error signing meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
        this.$ktoast.showError(err, true);
      } finally {
        this.isSubmitting = false;
      }
    },

    getMarkdown(str) {
      return markdown(str);
    },
  },
};
</script>
